import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useLocation, useNavigate } from 'react-router-dom'
import CurrentClientContext from '../../providers/CurrentClientProvider'
import TipContext from '../../providers/GlobalTip'
import PlanService from '../../services/PlanService'
import ModifyStyle from '../../components/plan/PlanStyle'
import NewPlanStyle from '../../components/plan/NewPlanStyle'
import PlanViewModeToggle from '../../components/plan/PlanViewModeToggle'
import PlanEditMonthlyTableShell from '../../components/plan/PlanEditMonthlyTableShell'
import PlanEditWeeklyTableShell from '../../components/plan/PlanEditWeeklyTableShell'
import PlanEditActionBar from '../../components/plan/PlanEditActionBar'
import PlanLeftPanel from '../../components/plan/PlanLeftPanel'
import PlanLeftPanelControls from '../../components/plan/PlanLeftPanelControls'
import ClientService from '../../services/ClientService'
import LivePlanEditEmbed from '../../components/plan/LivePlanEditEmbed'
import { buildWeekToMonthDayCountsFromDailyValues, safeJsonParseArray, rollupWeeklyToMonthly, deriveMonthlyInflows } from '../../lib/PlannerRollup'
import { buildDailyIndex } from '../../lib/PlannerDailyModel'
import { deriveMonthlyMarkdownPercent, sumMonthlyMarkdownPlan } from '../../lib/PlannerMonthlyMarkdown'
import { locationClassHasWeeklyPlanSettings, shouldFallbackWeeklyViewToMonthly } from '../../lib/PlannerWeeklyAvailability'
import { filterPlannerRowsForEpochMonth, getMaxPlanVersionForEpochMonth, getPlannerRowEpochMonthAnchor } from '../../lib/PlannerVersionScope'
import { getClientFeatureFlagEnabled } from '../../services/ClientFeatureFlagUtils'

const getQueryParams = (search) => {
  const params = new URLSearchParams(search)
  const queryParams = {}
  for (let [key, value] of params.entries()) {
    queryParams[key] = value
  }
  return queryParams
}

const STORAGE_KEYS = {
  locationId: 'planEdit.selectedLocationId',
  locationClassId: 'planEdit.selectedLocationClassId',
  planId: 'planEdit.selectedPlanId',
}

export const predictNextPlanVersion = (maxVersion) => {
  if (maxVersion === null || typeof maxVersion === 'undefined' || maxVersion === '') return null
  const numeric = Number(maxVersion)
  if (!Number.isFinite(numeric)) return null
  return Number((Math.round((numeric + 0.1) * 10) / 10).toFixed(1))
}

export const resolvePlannerSaveStrategy = ({ hasWeeklyPlanning, monthlyPayload, weeklyPayload, isFuturePlan = false }) => {
  if (!monthlyPayload && !weeklyPayload) return 'none'
  if (monthlyPayload && weeklyPayload) return 'atomic'
  if (!hasWeeklyPlanning && monthlyPayload && !weeklyPayload) {
    return isFuturePlan ? 'monthly-only-future-unsupported' : 'monthly-only'
  }
  return 'missing-required'
}

export const resolvePlannerVersionOption = ({
  planOptions = [],
  preferredPlanVersion = null,
  selectedPlanOptionValue = '0',
  planVersion = null,
  allowCarryoverSelection = true,
}) => {
  const safePlanOptions = Array.isArray(planOptions) ? planOptions : []
  if (!safePlanOptions.length) return null

  let nextSelected = null
  if (preferredPlanVersion !== null && typeof preferredPlanVersion !== 'undefined') {
    nextSelected = safePlanOptions.find((p) => Number(p.plan_version) === Number(preferredPlanVersion)) || null
  }
  if (!nextSelected && allowCarryoverSelection && selectedPlanOptionValue && selectedPlanOptionValue !== '0') {
    nextSelected = safePlanOptions.find((p) => String(p.optionValue) === String(selectedPlanOptionValue)) || null
  }
  if (!nextSelected && allowCarryoverSelection && planVersion !== null && typeof planVersion !== 'undefined') {
    nextSelected = safePlanOptions.find((p) => Number(p.plan_version) === Number(planVersion)) || null
  }
  if (!nextSelected) {
    const normal = safePlanOptions.filter((p) => Number(p.plan_version) < 1000)
    nextSelected = normal.length ? normal[normal.length - 1] : safePlanOptions[safePlanOptions.length - 1]
  }

  return nextSelected || null
}

export const shouldCarryOverPlannerVersionSelection = ({
  currentClientId = null,
  lastSelectedClientId = null,
  currentLocationClassId = null,
  lastSelectedLocationClassId = null,
}) => {
  const currentClient = Number(currentClientId || 0)
  const lastClient = Number(lastSelectedClientId || 0)
  const currentClass = Number(currentLocationClassId || 0)
  const lastClass = Number(lastSelectedLocationClassId || 0)

  if (!Number.isFinite(currentClient) || currentClient <= 0) return false
  if (!Number.isFinite(currentClass) || currentClass <= 0) return false

  return currentClient === lastClient && currentClass === lastClass
}

const padPlannerDatePart = (value) => String(value).padStart(2, '0')

export const formatPlannerCreatedTimestamp = (value) => {
  const raw = value?.date || value?.value || value
  if (!raw) return ''

  const parsed = raw instanceof Date ? raw : new Date(raw)
  if (!Number.isFinite(parsed.getTime())) return ''

  return `${parsed.getUTCFullYear()}-${padPlannerDatePart(parsed.getUTCMonth() + 1)}-${padPlannerDatePart(parsed.getUTCDate())} ${padPlannerDatePart(parsed.getUTCHours())}:${padPlannerDatePart(parsed.getUTCMinutes())} UTC`
}

const safeLsGet = (k) => {
  try {
    return localStorage.getItem(k)
  } catch (e) {
    return null
  }
}

const safeLsSet = (k, v) => {
  try {
    if (v === null || typeof v === 'undefined') {
      localStorage.removeItem(k)
    } else {
      localStorage.setItem(k, String(v))
    }
  } catch (e) { }
}

const getStoredPositiveId = (key) => {
  const raw = safeLsGet(key)
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

const hasMeaningfulDailyDelta = (currentByDate, baseByDate) => {
  if (!currentByDate || !baseByDate || typeof currentByDate.get !== 'function' || typeof baseByDate.get !== 'function') return false
  const keys = new Set([...Array.from(currentByDate.keys()), ...Array.from(baseByDate.keys())])
  const eps = 0.000001
  for (const dt of keys) {
    const cur = currentByDate.get(dt)
    const base = baseByDate.get(dt)
    const salesDiff = Math.abs(Number(cur?.salesPlan || 0) - Number(base?.salesPlan || 0))
    const mdDiff = Math.abs(Number(cur?.markdownPlan || 0) - Number(base?.markdownPlan || 0))
    const invDiff = Math.abs(Number(cur?.inventory || 0) - Number(base?.inventory || 0))
    if (salesDiff > eps || mdDiff > eps || invDiff > eps) return true
  }
  return false
}

const hasMeaningfulEdits = (editsByKey) => {
  const entries = Object.values(editsByKey || {})
  return entries.some((edit) => {
    if (!edit || typeof edit !== 'object') return false
    return Object.keys(edit).some((k) => k !== 'ts' && typeof edit[k] !== 'undefined')
  })
}

const normalizeLivePlansByLocation = (raw) => {
  const source = raw?.locations ?? raw?.data ?? raw ?? {}

  if (source && typeof source === 'object' && !Array.isArray(source)) {
    const values = Object.values(source)
    const looksLikeLocationMap = values.some((item) => {
      return item && typeof item === 'object' && (
        typeof item?.location_id !== 'undefined'
        || typeof item?.location_name !== 'undefined'
        || typeof item?.location_code !== 'undefined'
        || typeof item?.classes !== 'undefined'
      )
    })
    if (looksLikeLocationMap) return source
  }

  if (!Array.isArray(source)) return {}

  return source.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc
    const locationId = item?.location_id ?? item?.locationId ?? item?.id
    const normalizedId = Number(locationId)
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return acc
    acc[String(normalizedId)] = {
      ...item,
      location_id: normalizedId,
    }
    return acc
  }, {})
}

export const buildWeeklyMonthlyActualSyncDiagnostics = ({
  monthlyBaseUi = [],
  weeklyActualSalesByWeek = {},
  weekToMonthDayCounts = new Map(),
}) => {
  const monthlyActualByMonth = new Map()
    ; (Array.isArray(monthlyBaseUi) ? monthlyBaseUi : []).forEach((row) => {
      const epochMonth = Number(row?.epochMonth)
      if (!Number.isFinite(epochMonth)) return
      monthlyActualByMonth.set(String(epochMonth), Number(row?.salesActual || 0))
    })

  const weeklyActualByMonth = new Map()
  for (const [weekKey, monthMap] of (weekToMonthDayCounts || new Map()).entries()) {
    const totalDays = Array.from(monthMap.values()).reduce((acc, value) => acc + Number(value || 0), 0)
    const weeklySalesActual = Number(weeklyActualSalesByWeek?.[String(weekKey)] ?? weeklyActualSalesByWeek?.[Number(weekKey)] ?? 0)
    if (!(totalDays > 0) || !(weeklySalesActual || 0)) continue

    for (const [epochMonth, days] of monthMap.entries()) {
      const monthKey = String(epochMonth)
      const share = Number(days || 0) / totalDays
      weeklyActualByMonth.set(monthKey, (weeklyActualByMonth.get(monthKey) || 0) + (weeklySalesActual * share))
    }
  }

  const monthKeys = Array.from(new Set([
    ...Array.from(monthlyActualByMonth.keys()),
    ...Array.from(weeklyActualByMonth.keys()),
  ])).sort((left, right) => Number(left) - Number(right))

  const rows = monthKeys.map((monthKey) => {
    const monthlyActual = Number(monthlyActualByMonth.get(monthKey) || 0)
    const weeklyActual = Number(weeklyActualByMonth.get(monthKey) || 0)
    const diff = weeklyActual - monthlyActual
    return {
      epochMonth: Number(monthKey),
      monthlyActual,
      weeklyActual,
      diff,
      ratio: monthlyActual > 0 ? (weeklyActual / monthlyActual) : null,
    }
  })

  const mismatches = rows.filter((row) => Math.abs(row.diff) > 0.5)

  return {
    rows,
    mismatches,
    monthCount: rows.length,
    mismatchCount: mismatches.length,
    maxAbsDiff: mismatches.reduce((max, row) => Math.max(max, Math.abs(row.diff)), 0),
  }
}

function LivePlanModifyWeekly() {

  const location = useLocation()
  const navigate = useNavigate()
  const query = getQueryParams(location.search)
  const hasLoadedInitialLocationClass = useRef(!query?.lci)

  const currentClientContext = CurrentClientContext.useClient()
  const messageContext = TipContext.useMessage()

  const featureEnabled = getClientFeatureFlagEnabled(currentClientContext?.client, 'weekly-plan-ui')
  const isV3Route = location.pathname.startsWith('/v3/')

  // Wait until feature flags are loaded before deciding to redirect.
  // featureFlags being a non-null object signals the ClientMiddleware has finished hydrating.
  const plannerFlagsHydrated =
    (typeof currentClientContext?.client?.featureFlagsMap === 'object' &&
      currentClientContext?.client?.featureFlagsMap !== null) ||
    (typeof currentClientContext?.client?.featureFlags === 'object' &&
      currentClientContext?.client?.featureFlags !== null)

  // If the current client does not have the weekly-plan-ui flag, redirect them to the
  // legacy planning screen instead of showing a dead-end error message.
  useEffect(() => {
    if (plannerFlagsHydrated && !featureEnabled && currentClientContext?.client?.ClientCode) {
      navigate(
        isV3Route
          ? `/v3/client/${currentClientContext.client.ClientCode}/live-plan-edit`
          : `/clients/${currentClientContext.client.ClientCode}/live-plan-edit`,
        { replace: true },
      )
    }
  }, [plannerFlagsHydrated, featureEnabled, currentClientContext?.client?.ClientCode, isV3Route, navigate])

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('planEdit.viewMode') || 'monthly')
  const [calcMode, setCalcMode] = useState(() => localStorage.getItem('planEdit.calcMode') || 'allocate')
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => localStorage.getItem('planEdit.leftPanelCollapsed') === 'true')
  const [annualHeaderCollapsed, setAnnualHeaderCollapsed] = useState(() => localStorage.getItem('planEdit.annualHeaderCollapsed') === 'true')

  const [epochMonth, setEpochMonth] = useState(PlanService.getTodayEpochMonth())
  const [epochMonthOptions, setEpochMonthOptions] = useState([])

  const [clientBasicData, setClientBasicData] = useState({})
  const [locationOptions, setLocationOptions] = useState([])
  const [classOptions, setClassOptions] = useState([])
  const [planOptions, setPlanOptions] = useState([])

  const [selectedLocationId, setSelectedLocationId] = useState(() => getStoredPositiveId(STORAGE_KEYS.locationId))
  const [selectedLocationClassId, setSelectedLocationClassId] = useState(() => getStoredPositiveId(STORAGE_KEYS.locationClassId))
  const [selectedPlanId, setSelectedPlanId] = useState(0)
  const [selectedPlanOptionValue, setSelectedPlanOptionValue] = useState(() => safeLsGet(STORAGE_KEYS.planId) || '0')
  const [planVersion, setPlanVersion] = useState(null)
  const [preferredPlanVersion, setPreferredPlanVersion] = useState(null)

  const [reloadNonce, setReloadNonce] = useState(0)

  // Weekly context needs an epochWeek anchor + a weeklyPlanSettingsId.
  // epochWeek is optional for the API (backend can default to current), but we keep it in state for future UX.
  const epochWeek = useMemo(() => {
    // Accept an explicit querystring anchor if provided, else let backend default by passing null.
    return Number(query?.epochWeek || 0) || null
  }, [query?.epochWeek])

  const [monthlyPlanSettingsId, setMonthlyPlanSettingsId] = useState(null)
  const [weeklyPlanSettingsId, setWeeklyPlanSettingsId] = useState(null)

  const [latestMonthlyPlans, setLatestMonthlyPlans] = useState([])
  const [effectiveEpochWeek, setEffectiveEpochWeek] = useState(null)
  const [latestMonthlyPlansLoaded, setLatestMonthlyPlansLoaded] = useState(false)
  const weeklyFallbackNoticeShownRef = useRef(false)
  const weeklyMonthlyActualSyncSigRef = useRef('')

  const expectedMaxVersion = useMemo(() => {
    return getMaxPlanVersionForEpochMonth(latestMonthlyPlans, {
      locationClassId: selectedLocationClassId,
      epochMonth,
      isFuture: false,
    })
  }, [latestMonthlyPlans, selectedLocationClassId, epochMonth])

  const expectedMaxVersionFuture = useMemo(() => {
    return getMaxPlanVersionForEpochMonth(latestMonthlyPlans, {
      locationClassId: selectedLocationClassId,
      epochMonth,
      isFuture: true,
    })
  }, [latestMonthlyPlans, selectedLocationClassId, epochMonth])


  const [clientList, setClientList] = useState([])
  const [panelTabs, setPanelTabs] = useState([])
  const [panelChartData, setPanelChartData] = useState(new Map())
  const [weeklyChartLabels, setWeeklyChartLabels] = useState([])
  const [weeklyChartData, setWeeklyChartData] = useState(new Map())
  const [weeklyPeriodData, setWeeklyPeriodData] = useState([])

  const monthLabels = useMemo(() => {
    const start = Number(epochMonth || 0)
    if (!start) return Array.from({ length: 12 }, (_, i) => `M${i + 1}`)
    return Array.from({ length: 12 }, (_, i) => PlanService.getEpochMonthAsMMMYY(start + i))
  }, [epochMonth])

  // Shared per-session editor draft (cleared on hard refresh).
  const [plannerSession, setPlannerSession] = useState(() => ({
    weeklyEditsByWeek: {},
    monthlyEditsByMonth: {},
    weekToMonthDayCounts: new Map(),
    weeklyActualSalesByWeek: {},
    dailyIndex: null,
    dailyByDate: new Map(),
    datesByWeek: new Map(),
    datesByMonth: new Map(),
    calcMode: localStorage.getItem('planEdit.calcMode') || 'allocate',
    setDailyByDate: null,
  }))

  const updateWeeklyEdit = (epochWeekKey, patch) => {
    const ew = String(epochWeekKey)
    const ts = Date.now()
    setPlannerSession((prev) => {
      const next = { ...prev }
      const cur = prev.weeklyEditsByWeek?.[ew] || {}
      next.weeklyEditsByWeek = { ...(prev.weeklyEditsByWeek || {}), [ew]: { ...cur, ...patch, ts } }
      return next
    })
  }

  const updateMonthlyEdit = (epochMonthKey, patch) => {
    const em = String(epochMonthKey)
    const ts = Date.now()
    setPlannerSession((prev) => {
      const next = { ...prev }
      const cur = prev.monthlyEditsByMonth?.[em] || {}
      next.monthlyEditsByMonth = { ...(prev.monthlyEditsByMonth || {}), [em]: { ...cur, ...patch, ts } }
      return next
    })
  }

  const handleMonthlyBaseUi = (baseUiWindow) => {
    if (!Array.isArray(baseUiWindow) || baseUiWindow.length !== 12) return
    setPlannerSession((prev) => ({
      ...prev,
      monthlyBaseUi: baseUiWindow,
    }))

    // Drive the left-panel chart directly from the monthly grid data.
    // This bypasses the unreliable hidden legacy embed (LivePlanEditEmbed) which
    // was the old source for panelChartData and frequently failed to populate it.
    const m = new Map()
    m.set('SalesPlan', baseUiWindow.map((row) => Number(row?.salesPlan || 0)))
    m.set('InventoryBomPlan', baseUiWindow.map((row) => Number(row?.planInvBOM || 0)))
    m.set('MarkdownsPlan', baseUiWindow.map((row) => Number(row?.markdownPlan || 0)))
    m.set('InflowPlanRetail', baseUiWindow.map((row) => Number(row?.planInflowRetail || 0)))
    setPanelChartData(m)

    // Build TrendTargetBox tabs from new-planner data so the Plan column always
    // shows current values regardless of whether the legacy embed has loaded.
    // The legacy embed reads from the reporting pipeline (stale) whereas mergedData
    // here reflects the live session state including any unsaved edits.
    const fmtNum = (v) => {
      const n = Number(v || 0)
      if (!Number.isFinite(n)) return ''
      return Math.round(n).toLocaleString('en-US')
    }
    const current = baseUiWindow[0] || {}
    const annualSalesPlan = baseUiWindow.reduce((s, r) => s + Number(r?.salesPlan || 0), 0)
    const annualInflowCostPlan = baseUiWindow.reduce((s, r) => s + Number(r?.planInflowCost || 0), 0)
    const pctOfPlan = (actual, plan) => {
      const a = Number(actual || 0), p = Number(plan || 0)
      return (a > 0 && p > 0) ? `${Math.round((a / p) * 100)}%` : 'N/A'
    }

    // _tabs must be an array with string keys to match the shape PlanEditTable produces.
    const _tabs = []
    _tabs['Targets'] = {
      cash: { actual: '', plan: fmtNum(annualSalesPlan - annualInflowCostPlan) },
      GMROI: { actual: '', plan: '' },
      CMROI: { actual: '', plan: '' },
      MMU: { actual: '', plan: '' },
      Efficiency: { actual: '', plan: '' },
    }
    _tabs['Trend'] = {
      sales: { actual: '', trend: '', plan: fmtNum(current?.salesPlan), percentOfPlan: pctOfPlan(current?.salesActual, current?.salesPlan) },
      markdowns: { actual: '', trend: '', plan: fmtNum(current?.markdownPlan), percentOfPlan: pctOfPlan(current?.markdownActual, current?.markdownPlan) },
      inventory: { actual: '', trend: '', plan: fmtNum(current?.planInvBOM), percentOfPlan: 'N/A' },
      calc: { actual: '', trend: '', plan: '', percentOfPlan: '' },
      calc3: { actual: '', trend: '', plan: '', percentOfPlan: '' },
      linear: { actual: '', trend: '', plan: '', percentOfPlan: '' },
      L12: { actual: '', trend: fmtNum(annualSalesPlan), plan: '', percentOfPlan: '' },
    }
    const emptyBox = { cash: { actual: '', plan: '' }, inflow: { actual: '', plan: '' }, sales: { actual: '', plan: '' }, markdowns: { actual: '', plan: '' }, freshness: { actual: '', plan: '' } }
    _tabs['30'] = emptyBox
    _tabs['60'] = emptyBox
    _tabs['90'] = emptyBox
    setPanelTabs(_tabs)
  }

  const handleMonthlyContextLoaded = (monthlyRows) => {
    // monthlyRows: the raw monthly grid data items; we need daily_values from ALL rows
    // (one row per month) merged into one flat array to build the full 12-month daily canonical index.
    const allDaily = []
    const seenDates = new Set()
      ; (Array.isArray(monthlyRows) ? monthlyRows : []).forEach((r) => {
        const parsed = safeJsonParseArray(r?.daily_values)
        parsed.forEach((d) => {
          const dt = String(d?.date || '')
          if (dt && !seenDates.has(dt)) {
            seenDates.add(dt)
            allDaily.push(d)
          }
        })
      })
    const daily = allDaily
    const { weekToMonthDayCounts } = buildWeekToMonthDayCountsFromDailyValues(daily)
    const idx = buildDailyIndex(daily)

    // Prefer building the monthly base window from the most recent monthly plan rows.
    // The UI grid window may be derived and can be zero depending on the current canonical source.
    const latestByEpochMonth = new Map()
      ; (Array.isArray(monthlyRows) ? monthlyRows : []).forEach((r) => {
        const em = Number(r?.plan_epoch_month ?? r?.epoch_month ?? r?.EpochMonth ?? r?.epochMonth)
        if (!Number.isFinite(em)) return
        // If multiple rows exist for same month, keep the highest version.
        const cur = latestByEpochMonth.get(em)
        const v = Number(r?.plan_version ?? r?.planVersion ?? 0)
        const curV = Number(cur?.plan_version ?? cur?.planVersion ?? 0)
        if (!cur || v >= curV) latestByEpochMonth.set(em, r)
      })

    const monthStart = Number(epochMonth || 0)
    const monthlyBaseUi = Array.from({ length: 12 }, (_, idx) => {
      const em = monthStart + idx
      const r = latestByEpochMonth.get(em) || {}

      return {
        epochMonth: em,
        salesPlan: Number(r?.sales_monthly_plan ?? r?.salesMonthlyPlan ?? r?.salesPlan ?? r?.sales_plan ?? 0),
        markdownPlan: Number(r?.markdown_plan ?? r?.markdownPlan ?? r?.markdown_plan_dollars ?? r?.markdowns_plan ?? 0),
        planInvBOM: Number(r?.inventory_bom_plan ?? r?.inventoryBomPlan ?? r?.planInvBOM ?? 0),
        planInflowRetail: Number(r?.inflow_retail_plan ?? r?.inflowRetailPlan ?? 0),
        planPercent: Number(r?.sales_monthly_percentage_plan ?? r?.salesMonthlyPercentagePlan ?? 0) * 100,
        planImuPct: Number(r?.imu_plan ?? r?.imuPlan ?? 0),
        salesAnnualPlan: Number(r?.sales_annual_plan ?? r?.salesAnnualPlan ?? 0),
      }
    })

    // Debug: confirm we have plan dollars in the base.
    try {
      const sample = (monthlyBaseUi || []).slice(0, 3).map((m) => ({ em: m.epochMonth, salesPlan: m.salesPlan, mdPlan: m.markdownPlan }))
      const totals = (monthlyBaseUi || []).reduce(
        (acc, m) => {
          acc.sales += Number(m?.salesPlan || 0)
          acc.md += Number(m?.markdownPlan || 0)
          return acc
        },
        { sales: 0, md: 0 }
      )
      const nonZeroMonths = (monthlyBaseUi || []).filter((m) => Number(m?.salesPlan || 0) !== 0).length
      const print12 = (monthlyBaseUi || []).map((m) => ({ em: m.epochMonth, sales: m.salesPlan, md: m.markdownPlan }))
      console.log('[planner][monthly] context loaded', {
        rows: Array.isArray(monthlyRows) ? monthlyRows.length : 0,
        dailyValues: allDaily.length,
        baseUiLen: monthlyBaseUi.length,
        sample,
        totals,
        nonZeroMonths,
        firstMonth: monthlyBaseUi?.[0]?.epochMonth,
        lastMonth: monthlyBaseUi?.[monthlyBaseUi.length - 1]?.epochMonth,
      })
      console.log('[planner][monthly] baseUi months', print12)
    } catch (e) { }

    setPlannerSession((prev) => ({
      ...prev,
      weekToMonthDayCounts: weekToMonthDayCounts || new Map(),
      monthlyBase: Array.isArray(monthlyRows) ? monthlyRows : prev.monthlyBase,
      monthlyBaseUi,
      // Fresh backend context should clear stale local edit patches.
      monthlyEditsByMonth: {},
      weeklyEditsByWeek: {},
      dailyIndex: idx,
      dailyByDate: idx?.byDate || new Map(),
      datesByWeek: idx?.datesByWeek || new Map(),
      datesByMonth: idx?.datesByMonth || new Map(),
    }))
  }

  const handleWeeklyContextLoaded = ({ epochWeeks, salesActualByWeek, anchorEpochWeek }) => {
    setPlannerSession((prev) => ({
      ...prev,
      weeklyActualSalesByWeek: salesActualByWeek || prev.weeklyActualSalesByWeek,
      weeklyEpochWeeks: Array.isArray(epochWeeks) ? epochWeeks : prev.weeklyEpochWeeks,
      weeklyEpochWeekAnchor: Number(anchorEpochWeek || 0) || prev.weeklyEpochWeekAnchor || null,
    }))

    if (Number(anchorEpochWeek || 0) > 0) {
      setEffectiveEpochWeek(Number(anchorEpochWeek))
    }
  }

  const handleDailyByDateChange = (nextDailyByDate) => {
    if (!nextDailyByDate || typeof nextDailyByDate.get !== 'function') return
    setPlannerSession((prev) => ({
      ...prev,
      dailyByDate: nextDailyByDate,
    }))
  }

  useEffect(() => {
    // Provide a stable setter function to children through plannerSession.
    // (This avoids having to thread another prop through every component.)
    setPlannerSession((prev) => {
      if (prev.setDailyByDate) return prev
      return {
        ...prev,
        setDailyByDate: handleDailyByDateChange,
      }
    })
  }, [])

  useEffect(() => {
    localStorage.setItem('planEdit.viewMode', viewMode)
  }, [viewMode])

  useEffect(() => {
    localStorage.setItem('planEdit.calcMode', calcMode)

    // Ensure the daily-canonical editor uses the currently selected calc mode.
    setPlannerSession((prev) => ({
      ...prev,
      calcMode,
    }))
  }, [calcMode])

  useEffect(() => {
    const currentClientId = currentClientContext?.client?.Id
    const currentLocationClassId = selectedLocationClassId
    const monthlyBaseUi = Array.isArray(plannerSession?.monthlyBaseUi) ? plannerSession.monthlyBaseUi : []
    const weeklyActualSalesByWeek = plannerSession?.weeklyActualSalesByWeek || {}
    const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts || new Map()

    if (!monthlyBaseUi.length || !weekToMonthDayCounts?.size) return

    const signature = [
      monthlyBaseUi.length,
      Object.keys(weeklyActualSalesByWeek).length,
      weekToMonthDayCounts.size,
      monthlyBaseUi.map((row) => `${row?.epochMonth}:${Number(row?.salesActual || 0)}`).join('|'),
      Object.entries(weeklyActualSalesByWeek).slice(0, 8).map(([week, value]) => `${week}:${Number(value || 0)}`).join('|'),
    ].join('::')

    if (signature === weeklyMonthlyActualSyncSigRef.current) return
    weeklyMonthlyActualSyncSigRef.current = signature

    const diagnostics = buildWeeklyMonthlyActualSyncDiagnostics({
      monthlyBaseUi,
      weeklyActualSalesByWeek,
      weekToMonthDayCounts,
    })

    if (!diagnostics.mismatchCount) {
      console.info('[planner][diag] weekly/monthly actual sales in sync', {
        clientId: currentClientId,
        locationClassId: currentLocationClassId,
        monthCount: diagnostics.monthCount,
        maxAbsDiff: diagnostics.maxAbsDiff,
      })
      return
    }

    console.warn('[planner][diag] weekly/monthly actual sales out of sync', {
      clientId: currentClientId,
      locationClassId: currentLocationClassId,
      monthCount: diagnostics.monthCount,
      mismatchCount: diagnostics.mismatchCount,
      maxAbsDiff: diagnostics.maxAbsDiff,
      mismatches: diagnostics.mismatches.map((row) => ({
        epochMonth: row.epochMonth,
        monthlyActual: Number(row.monthlyActual.toFixed(2)),
        weeklyActual: Number(row.weeklyActual.toFixed(2)),
        diff: Number(row.diff.toFixed(2)),
        ratio: row.ratio === null ? null : Number(row.ratio.toFixed(4)),
      })),
    })
  }, [currentClientContext?.client?.Id, selectedLocationClassId, plannerSession?.monthlyBaseUi, plannerSession?.weeklyActualSalesByWeek, plannerSession?.weekToMonthDayCounts])

  useEffect(() => {
    localStorage.setItem('planEdit.leftPanelCollapsed', String(leftPanelCollapsed))
  }, [leftPanelCollapsed])

  useEffect(() => {
    localStorage.setItem('planEdit.annualHeaderCollapsed', String(annualHeaderCollapsed))
  }, [annualHeaderCollapsed])

  useEffect(() => {
    if (!epochMonthOptions.length && epochMonth) {
      let _epochMonth = Number(epochMonth)
      let _epochMonthOptions = [_epochMonth + 1]
      for (let i = 0; i < 12; i++) {
        _epochMonthOptions.push(_epochMonth - i)
      }
      setEpochMonthOptions(_epochMonthOptions)
    }
  }, [epochMonth, epochMonthOptions.length])

  const getClientLivePlans = async () => {
    try {
      const data = await PlanService.getClientLivePlans(currentClientContext.client.Id, epochMonth ?? null)
      const normalized = normalizeLivePlansByLocation(data)
      console.info('[planner][diag] getClientLivePlans', {
        clientId: currentClientContext?.client?.Id,
        epochMonth,
        rawType: Array.isArray(data) ? 'array' : typeof data,
        rawKeys: data && typeof data === 'object' ? Object.keys(data).slice(0, 8) : [],
        normalizedLocationCount: Object.keys(normalized || {}).length,
      })
      setClientBasicData(normalized)
    } catch (e) {
      console.error('[planner][diag] getClientLivePlans failed', {
        clientId: currentClientContext?.client?.Id,
        epochMonth,
        message: e?.message,
      })
      messageContext.show(e?.response?.data?.message || 'Failed to load client live plans')
    }
  }

  const clientId = currentClientContext?.client?.Id
  const lastPlanSelectionClientIdRef = useRef(null)
  const lastPlanSelectionLocationClassIdRef = useRef(null)
  const previousClientIdRef = useRef(clientId || null)

  const resetPlanSelection = ({ clearPreferredPlanVersion = true } = {}) => {
    setSelectedPlanOptionValue('0')
    setSelectedPlanId(0)
    setPlanVersion(null)
    setMonthlyPlanSettingsId(null)
    setWeeklyPlanSettingsId(null)
    if (clearPreferredPlanVersion) {
      setPreferredPlanVersion(null)
    }
  }

  useEffect(() => {
    const previousClientId = previousClientIdRef.current
    if (previousClientId !== null && Number(previousClientId) !== Number(clientId || null)) {
      resetPlanSelection()
      lastPlanSelectionClientIdRef.current = null
      lastPlanSelectionLocationClassIdRef.current = null
    }
    previousClientIdRef.current = clientId || null
  }, [clientId])

  useEffect(() => {
    if (clientId) {
      getClientLivePlans()
    }
  }, [clientId, epochMonth])

  useEffect(() => {
    const loadLatestMonthlyPlans = async () => {
      if (!clientId) {
        setLatestMonthlyPlans([])
        setLatestMonthlyPlansLoaded(false)
        return
      }

      try {
        const resp = await PlanService.getLatestMonthlyPlans(clientId)
        const data = resp?.data ?? resp ?? []
        console.info('[planner][diag] getLatestMonthlyPlans', {
          clientId,
          rowCount: Array.isArray(data) ? data.length : 0,
          firstRow: Array.isArray(data) && data.length ? {
            location_class_id: data[0]?.location_class_id,
            monthly_plan_settings_id: data[0]?.monthly_plan_settings_id,
            weekly_plan_settings_id: data[0]?.weekly_plan_settings_id,
            plan_version: data[0]?.plan_version,
          } : null,
        })
        setLatestMonthlyPlans(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error('[planner][diag] getLatestMonthlyPlans failed', {
          clientId,
          message: e?.message,
        })
        // Do NOT reset latestMonthlyPlans to [] on a transient refresh failure.
        // Keeping stale data is far better than wiping plan options (which causes
        // planVersion to reset to null and unmounts the grid component).
      } finally {
        setLatestMonthlyPlansLoaded(true)
      }
    }

    setLatestMonthlyPlansLoaded(false)
    weeklyFallbackNoticeShownRef.current = false
    loadLatestMonthlyPlans()
  }, [clientId, reloadNonce])

  useEffect(() => {
    const _locationOptions = []
    if (clientBasicData) {
      Object.entries(clientBasicData).forEach(([key, item]) => {
        const locationId = Number(item?.location_id ?? item?.locationId ?? key)
        if (!Number.isFinite(locationId) || locationId <= 0) return
        _locationOptions.push({
          location_id: locationId,
          name: item?.location_name ?? item?.locationName ?? `Location ${locationId}`,
          code: item?.location_code ?? item?.locationCode ?? String(locationId),
        })
      })
    }

    _locationOptions.sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))
    setLocationOptions(_locationOptions)

    const hasSelectedLocation = _locationOptions.some((item) => String(item?.location_id) === String(selectedLocationId))
    if (_locationOptions.length && !hasSelectedLocation) {
      setSelectedLocationId(_locationOptions[0].location_id)
      return
    }

    if (!_locationOptions.length && selectedLocationId) {
      setSelectedLocationId(0)
    }
  }, [clientBasicData, selectedLocationId])

  useEffect(() => {
    const _classOptions = []
    if (selectedLocationId) {
      const loc = clientBasicData?.[selectedLocationId]
        || clientBasicData?.[String(selectedLocationId)]
        || Object.values(clientBasicData || {}).find((item) => String(item?.location_id) === String(selectedLocationId))

      const classes = loc?.classes ?? loc?.Classes
      if (classes && typeof classes === 'object') {
        const classEntries = Array.isArray(classes)
          ? classes.map((item, idx) => [String(item?.class_id ?? item?.classId ?? item?.id ?? idx), item])
          : Object.entries(classes)
        classEntries.forEach(([key, item]) => {
          const classId = Number(item?.class_id ?? item?.classId ?? item?.id ?? key)
          if (!Number.isFinite(classId) || classId <= 0) return
          _classOptions.push({
            class_id: classId,
            name: item?.class_name ?? item?.className ?? `Class ${classId}`,
            code: item?.class_code ?? item?.classCode ?? String(classId),
          })
        })
      }
    }

    _classOptions.sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))
    setClassOptions(_classOptions)

    if (!hasLoadedInitialLocationClass.current && _classOptions.length > 0) {
      const maybeLci = query?.lci
      if (maybeLci && _classOptions.find((c) => String(c.class_id) === String(maybeLci))) {
        setSelectedLocationClassId(maybeLci)
      } else {
        setSelectedLocationClassId(_classOptions[0].class_id)
      }
      hasLoadedInitialLocationClass.current = true
      return
    }

    const hasSelectedClass = _classOptions.some((item) => String(item?.class_id) === String(selectedLocationClassId))
    if (_classOptions.length && !hasSelectedClass) {
      setSelectedLocationClassId(_classOptions[0].class_id)
      return
    }

    if (!_classOptions.length && selectedLocationClassId) {
      setSelectedLocationClassId(0)
    }
  }, [selectedLocationId, clientBasicData, query?.lci, selectedLocationClassId])

  useEffect(() => {
    const rowsForClass = filterPlannerRowsForEpochMonth(latestMonthlyPlans, {
      locationClassId: selectedLocationClassId,
      epochMonth,
    })

    const resolveBestRowForVersion = (version) => {
      const candidates = rowsForClass
        .filter((r) => Number(r?.plan_version ?? 0) === Number(version))
        .sort((a, b) => {
          const aCreated = new Date(a?.created_date?.date || a?.created_date || 0).getTime()
          const bCreated = new Date(b?.created_date?.date || b?.created_date || 0).getTime()
          return bCreated - aCreated
        })

      if (!candidates.length) return null

      const currentEpochMonth = Number(epochMonth || 0)
      const exactAnchorMatch = candidates.find((row) => Number(getPlannerRowEpochMonthAnchor(row)) === currentEpochMonth)
      if (exactAnchorMatch) return exactAnchorMatch

      const coveringWindowMatch = candidates.find((row) => {
        const anchor = Number(getPlannerRowEpochMonthAnchor(row))
        return Number.isFinite(anchor) && currentEpochMonth >= anchor && currentEpochMonth <= (anchor + 11)
      })
      if (coveringWindowMatch) return coveringWindowMatch

      return candidates[0]
    }

    const byVersion = new Map()
    rowsForClass.forEach((r) => {
      const version = Number(r?.plan_version ?? 0)
      if (!Number.isFinite(version)) return
      if (!byVersion.has(version)) {
        const resolved = resolveBestRowForVersion(version) || r
        const monthlyPsid = String(resolved?.monthly_plan_settings_id || resolved?.plan_settings_id || resolved?.planSettingsId || '')
        const weeklyPsid = String(resolved?.weekly_plan_settings_id || '')
        const createdAt = resolved?.created_date?.date || resolved?.created_date || null

        byVersion.set(version, {
          optionValue: String(version),
          plan_id: monthlyPsid,
          plan_settings_id: monthlyPsid,
          monthly_plan_settings_id: monthlyPsid,
          weekly_plan_settings_id: weeklyPsid,
          plan_version: version,
          service_type_id: 1,
          is_future_plan: version >= 1000,
          created_date: createdAt,
        })
      }
    })

    const _planOptions = Array.from(byVersion.values()).sort((a, b) => Number(a.plan_version) - Number(b.plan_version))
    console.info('[planner][diag] planOptions resolved', {
      selectedLocationClassId,
      rowsForClass: rowsForClass.length,
      optionCount: _planOptions.length,
      versions: _planOptions.map((p) => p?.plan_version),
    })
    setPlanOptions(_planOptions)

    if (!_planOptions.length) {
      console.warn('[planner][diag] planOptions empty; resetting plan selection', {
        selectedLocationClassId,
      })
      setSelectedPlanOptionValue('0')
      setSelectedPlanId(0)
      setPlanVersion(null)
      setMonthlyPlanSettingsId(null)
      setWeeklyPlanSettingsId(null)
      return
    }

    const allowCarryoverSelection = shouldCarryOverPlannerVersionSelection({
      currentClientId: clientId,
      lastSelectedClientId: lastPlanSelectionClientIdRef.current,
      currentLocationClassId: selectedLocationClassId,
      lastSelectedLocationClassId: lastPlanSelectionLocationClassIdRef.current,
    })
    const nextSelected = resolvePlannerVersionOption({
      planOptions: _planOptions,
      preferredPlanVersion,
      selectedPlanOptionValue,
      planVersion,
      allowCarryoverSelection,
    })

    if (!nextSelected) return

    const resolvedRow = resolveBestRowForVersion(nextSelected.plan_version)
    const resolvedMonthlyPlanSettingsId = resolvedRow?.monthly_plan_settings_id || resolvedRow?.plan_settings_id || nextSelected.monthly_plan_settings_id || nextSelected.plan_settings_id
    const resolvedWeeklyPlanSettingsId = resolvedRow?.weekly_plan_settings_id || nextSelected.weekly_plan_settings_id || null

    setSelectedPlanOptionValue(nextSelected.optionValue)
    setSelectedPlanId(resolvedMonthlyPlanSettingsId || 0)
    setPlanVersion(nextSelected.plan_version)
    setMonthlyPlanSettingsId(resolvedMonthlyPlanSettingsId || null)
    setWeeklyPlanSettingsId(resolvedWeeklyPlanSettingsId)
    lastPlanSelectionClientIdRef.current = Number(clientId || 0)
    lastPlanSelectionLocationClassIdRef.current = Number(selectedLocationClassId || 0)

    if (preferredPlanVersion !== null && Number(nextSelected.plan_version) === Number(preferredPlanVersion)) {
      setPreferredPlanVersion(null)
    }
  }, [selectedLocationClassId, latestMonthlyPlans, selectedPlanOptionValue, planVersion, preferredPlanVersion, epochMonth, clientId])

  const selectedPlanMeta = useMemo(() => {
    if (!Array.isArray(planOptions) || !planOptions.length) return null

    if (selectedPlanOptionValue && selectedPlanOptionValue !== '0') {
      const byOptionValue = planOptions.find((option) => String(option?.optionValue) === String(selectedPlanOptionValue))
      if (byOptionValue) return byOptionValue
    }

    if (planVersion !== null && typeof planVersion !== 'undefined') {
      return planOptions.find((option) => Number(option?.plan_version) === Number(planVersion)) || null
    }

    return null
  }, [planOptions, selectedPlanOptionValue, planVersion])

  const selectedPlanCreatedLabel = useMemo(() => {
    return formatPlannerCreatedTimestamp(selectedPlanMeta?.created_date)
  }, [selectedPlanMeta])

  const shouldFallbackWeeklyToMonthly = useMemo(() => {
    return shouldFallbackWeeklyViewToMonthly({
      viewMode,
      selectedLocationClassId,
      latestMonthlyPlansLoaded,
      latestMonthlyPlans,
      epochMonth,
    })
  }, [viewMode, selectedLocationClassId, latestMonthlyPlansLoaded, latestMonthlyPlans, epochMonth])

  const hasWeeklyPlanningForSelectedClass = useMemo(() => {
    return locationClassHasWeeklyPlanSettings({
      latestMonthlyPlans,
      selectedLocationClassId,
      epochMonth,
    })
  }, [latestMonthlyPlans, selectedLocationClassId, epochMonth])

  const isWeeklyRestorePending = viewMode === 'weekly' && !latestMonthlyPlansLoaded
  const resolvedViewMode = (isWeeklyRestorePending || shouldFallbackWeeklyToMonthly) ? 'monthly' : viewMode

  useEffect(() => {
    if (!shouldFallbackWeeklyToMonthly || viewMode !== 'weekly') return
    setViewMode('monthly')
    if (!weeklyFallbackNoticeShownRef.current) {
      weeklyFallbackNoticeShownRef.current = true
      messageContext.show('Weekly view is not available for this location class yet. Switched back to Monthly.')
    }
  }, [shouldFallbackWeeklyToMonthly, viewMode, messageContext])

  const title = useMemo(() => {
    const clientName = currentClientContext?.client?.Name
    const clientCode = currentClientContext?.client?.ClientCode
    return clientName && clientCode ? `${clientName} (${clientCode})` : 'Weekly Plan Edit'
  }, [currentClientContext?.client?.Name, currentClientContext?.client?.ClientCode])

  const [savingPlanner, setSavingPlanner] = useState(false)

  const hasUnsavedPlannerChanges = useMemo(() => {
    const monthEdits = hasMeaningfulEdits(plannerSession?.monthlyEditsByMonth)
    const weekEdits = hasMeaningfulEdits(plannerSession?.weeklyEditsByWeek)
    const hasDailyDraft = plannerSession?.dailyByDate && plannerSession?.dailyIndex?.byDate
      ? hasMeaningfulDailyDelta(plannerSession.dailyByDate, plannerSession.dailyIndex.byDate)
      : false
    return monthEdits || weekEdits || hasDailyDraft
  }, [plannerSession])

  const buildMonthlySavePayload = () => {
    const baseMonthlyRaw = Array.isArray(plannerSession?.monthlyBase) ? plannerSession.monthlyBase : []
    const psid = (baseMonthlyRaw?.[0]?.plan_settings_id || baseMonthlyRaw?.[0]?.planSettingsId || monthlyPlanSettingsId || null)
    if (!psid) return null

    const idx = plannerSession?.dailyIndex
    const byDate = plannerSession?.dailyByDate
    const datesByMonth = plannerSession?.datesByMonth
    if (!idx || !byDate || typeof byDate.get !== 'function' || !datesByMonth || typeof datesByMonth.get !== 'function') {
      // Daily-canonical not ready yet.
      return null
    }

    const hasDailySalesMarkdownChanges = hasMeaningfulDailyDelta(byDate, idx?.byDate)
    const hasMonthlyEdits = hasMeaningfulEdits(plannerSession?.monthlyEditsByMonth)

    // Build a per-month set of which epoch months have actual daily value changes.
    // This is the key payload optimization: instead of sending dailyValues for all 12
    // months whenever ANY day changes (inflating v_months_json to ~108 KB), we only
    // include dailyValues for months that actually have changes. The BQ script already
    // handles absent monthly daily_values via COALESCE(sm.daily_values, lem.daily_values),
    // so months without dailyValues in the payload simply copy-forward the previous version.
    // Typical partial edits (1–3 months) reduce payload from ~108 KB to ~9–27 KB.
    const changedEpochMonths = new Set()
    if (hasDailySalesMarkdownChanges && idx?.byDate) {
      for (const [dt, rec] of byDate.entries()) {
        const base = idx.byDate.get(dt)
        if (!base) {
          // New date not in base — treat as changed.
          const em = rec?.epochMonth
          if (Number.isFinite(em)) changedEpochMonths.add(String(em))
          continue
        }
        const spDiff = Math.abs(Number(rec.salesPlan || 0) - Number(base.salesPlan || 0))
        const mdDiff = Math.abs(Number(rec.markdownPlan || 0) - Number(base.markdownPlan || 0))
        const invDiff = Math.abs(Number(rec.inventory || 0) - Number(base.inventory || 0))
        if (spDiff > 0.000001 || mdDiff > 0.000001 || invDiff > 0.000001) {
          const em = rec?.epochMonth
          if (Number.isFinite(em)) changedEpochMonths.add(String(em))
        }
      }
    }

    const start = Number(epochMonth)
    // Use the actual plan months from monthlyBaseUi — only save rows that have real plan data.
    const baseUiMonths = Array.isArray(plannerSession?.monthlyBaseUi) ? plannerSession.monthlyBaseUi : []
    // Build the epoch months list: start from the first plan month, cover through the last.
    const sortedPlanMonths = baseUiMonths.map((m) => m.epochMonth).filter(Number.isFinite).sort((a, b) => a - b)
    const epochMonthStart = sortedPlanMonths[0] ?? start
    const epochMonthEnd = sortedPlanMonths[sortedPlanMonths.length - 1] ?? (start + 11)
    const monthsForwardCount = epochMonthEnd - epochMonthStart
    const epochMonths = sortedPlanMonths.length
      ? sortedPlanMonths.map(String)
      : Array.from({ length: 12 }, (_, i) => String(start + i))

    // Stable inflow ratio from the current effective monthly window.
    const effectiveMonthly = rollupWeeklyToMonthly({
      monthlyBase: Array.isArray(plannerSession?.monthlyBaseUi) ? plannerSession.monthlyBaseUi : [],
      monthlyEdits: plannerSession?.monthlyEditsByMonth || {},
      weeklyEdits: plannerSession?.weeklyEditsByWeek || {},
      weekToMonthDayCounts: plannerSession?.weekToMonthDayCounts || new Map(),
      weeklyActualSalesByWeek: plannerSession?.weeklyActualSalesByWeek || {},
      calcMode: plannerSession?.calcMode || calcMode || 'allocate',
    })
    const effectiveMonthlyByEpochMonth = new Map((effectiveMonthly || []).map((m) => [String(m?.epochMonth), m]))

    // Roll up daily to month $ — fall back to the current effective monthly window for months without daily data.
    const monthRoll = (() => {
      const out = {}
      epochMonths.forEach((em) => (out[em] = { salesPlan: 0, markdownPlan: 0 }))
      for (const d of byDate.values()) {
        const em = d?.epochMonth
        if (!Number.isFinite(em)) continue
        const key = String(em)
        if (!out[key]) out[key] = { salesPlan: 0, markdownPlan: 0 }
        out[key].salesPlan += Number(d.salesPlan || 0)
        out[key].markdownPlan += Number(d.markdownPlan || 0)
      }
      // For months with no daily values, fall back to the effective monthly totals.
      epochMonths.forEach((em) => {
        const key = String(em)
        const effectiveMonth = effectiveMonthlyByEpochMonth.get(key)
        if (!out[key]) return
        if (out[key].salesPlan === 0 && Number(effectiveMonth?.salesPlan || 0) > 0) {
          out[key].salesPlan = Number(effectiveMonth?.salesPlan)
        }
        if (out[key].markdownPlan === 0 && Number(effectiveMonth?.markdownPlan || 0) > 0) {
          out[key].markdownPlan = Number(effectiveMonth?.markdownPlan)
        }
      })
      return out
    })()
    const staticAnnualSales = (effectiveMonthly || []).reduce((acc, row) => {
      if (acc > 0) return acc
      const v = Number(row?.salesAnnualPlan ?? 0)
      return v > 0 ? v : acc
    }, 0)
    const annualSales = staticAnnualSales > 0
      ? staticAnnualSales
      : epochMonths.reduce((acc, em) => acc + Number(monthRoll?.[String(em)]?.salesPlan || 0), 0)


    const annualMarkdown = sumMonthlyMarkdownPlan(epochMonths.map((em) => ({
      markdownPlan: Number(monthRoll?.[String(em)]?.markdownPlan || 0),
    })))

    const monthSnapshots = epochMonths.map((em) => {
      const salesPlan = Number(monthRoll?.[em]?.salesPlan || 0)
      const mdPlan = Number(monthRoll?.[em]?.markdownPlan || 0)

      // Inventory BOM (BOM) = first day in that month.
      const dates = datesByMonth.get(String(em)) || []
      const first = Array.isArray(dates) && dates.length ? dates[0] : null
      const invBomFromDaily = first ? Number(byDate.get(first)?.inventory || 0) : 0
      const effectiveMonth = effectiveMonthlyByEpochMonth.get(String(em)) || baseUiMonths.find((m) => String(m.epochMonth) === String(em))
      const effectiveInvBom = Number(effectiveMonth?.planInvBOM)
      const invBom = Number.isFinite(effectiveInvBom)
        ? effectiveInvBom
        : (first ? invBomFromDaily : 0)

      return {
        epochMonth: Number(em),
        salesPlan,
        markdownPlan: mdPlan,
        planInvBOM: invBom,
        planPercent: annualSales ? (salesPlan / annualSales) * 100 : 0,
        markdownPlanPercent: deriveMonthlyMarkdownPercent({
          markdownPlan: mdPlan,
          annualMarkdownPlan: annualMarkdown,
        }),
        currentPlanVersion: planVersion,
        imuPlan: Number(effectiveMonth?.imuPlan ?? 0),
        dailyValues: changedEpochMonths.has(String(em))
          ? (dates || []).map((dt) => {
            const r = byDate.get(dt)
            return {
              date: r?.date,
              epoch_week: r?.epochWeek,
              epoch_month: r?.epochMonth,
              sales_plan: Number(r?.salesPlan || 0),
              markdowns: Number(r?.markdownPlan || 0),
              inventory: Number(r?.inventory || 0),
              // Weight fields must round-trip: they drive PlannerMonthlyWeights
              // weighted distribution when the user edits a period total.
              sales_weight: r?.raw?.sales_weight,
              markdowns_weight: r?.raw?.markdowns_weight,
              monthly_sales_weight: r?.raw?.monthly_sales_weight,
              monthly_markdowns_weight: r?.raw?.monthly_markdowns_weight,
            }
          })
          : null,
      }
    })

    const monthInflows = deriveMonthlyInflows(monthSnapshots)

    const months = monthSnapshots.map((row, index) => {
      const planSS = Number(row?.salesPlan || 0) > 0 ? (Number(row?.planInvBOM || 0) / Number(row?.salesPlan || 0)) : 0

      return {
        epochMonth: Number(row?.epochMonth),
        planPercent: Number(row?.planPercent || 0),
        planSS,
        planInvBOM: Number(row?.planInvBOM || 0),
        markdownPlanPercent: Number(row?.markdownPlanPercent || 0),
        currentPlanVersion: row?.currentPlanVersion,

        // Full snapshot extras (backend may ignore until SQL is updated)
        salesMonthlyPlan: Number(row?.salesPlan || 0),
        markdownPlan: Number(row?.markdownPlan || 0),
        inflowRetailPlan: Number(monthInflows[index]?.planInflowRetail || 0),
        inflowCostPlan: Number(monthInflows[index]?.planInflowCost || 0),
        ...(row?.dailyValues ? { dailyValues: row.dailyValues } : {}),
      }
    })

    return {
      monthlyPlanSettingsId: String(psid),
      locationClassId: Number(selectedLocationClassId),
      currentPlanVersion: planVersion,
      epochMonthStart: epochMonthStart,
      monthsForward: monthsForwardCount,
      hasMonthlyEdits,
      hasDailySalesMarkdownChanges,
      months,
    }
  }

  const buildWeeklySavePayload = () => {
    if (!weeklyPlanSettingsId) return null

    const byDate = plannerSession?.dailyByDate
    const datesByWeek = plannerSession?.datesByWeek
    if (!byDate || typeof byDate.get !== 'function' || !datesByWeek || typeof datesByWeek.get !== 'function') {
      return null
    }

    const planningStartWeek = Number(plannerSession?.weeklyEpochWeekAnchor || effectiveEpochWeek || epochWeek || 0)
    if (!Number.isFinite(planningStartWeek) || planningStartWeek <= 0) {
      return null
    }

    const windowWeeks = Array.from({ length: 12 }, (_, i) => planningStartWeek + i)
    const epochWeekStart = planningStartWeek

    const weeks = windowWeeks.map((ew) => {
      const dates = datesByWeek.get(String(ew)) || []
      const salesPlan = (dates || []).reduce((acc, dt) => acc + Number(byDate.get(dt)?.salesPlan || 0), 0)
      const mdPlan = (dates || []).reduce((acc, dt) => acc + Number(byDate.get(dt)?.markdownPlan || 0), 0)
      const first = Array.isArray(dates) && dates.length ? dates[0] : null
      const invBow = first ? Number(byDate.get(first)?.inventory || 0) : 0

      const actualSales = Number(plannerSession?.weeklyActualSalesByWeek?.[String(ew)] ?? 0)
      const planPercent = actualSales ? (salesPlan / actualSales) * 100 : 0
      const mdPercent = salesPlan ? (mdPlan / salesPlan) * 100 : 0
      const planSS = salesPlan ? (invBow / salesPlan) : 0

      return {
        epochWeek: Number(ew),
        planPercent,
        planSS,
        planInvBOM: invBow,
        markdownPlanPercent: mdPercent,
        weeklySalesPlan: salesPlan,
        weeklyMarkdownsPlan: mdPlan,
      }
    })

    return {
      weeklyPlanSettingsId: String(weeklyPlanSettingsId),
      locationClassId: Number(selectedLocationClassId),
      currentPlanVersion: planVersion,
      epochWeekStart,
      weeksForward: 11,
      hasWeeklyEdits: hasMeaningfulEdits(plannerSession?.weeklyEditsByWeek),
      weeks,
    }
  }

  const handleUpdatePlan = async () => {
    try {
      if (!clientId) return
      if (!hasUnsavedPlannerChanges) {
        messageContext.show('No changes to save')
        return
      }
      const negativeInflowMonths = (plannerSession?.monthlyBaseUi || []).filter(
        (row) => Number(row?.planInflowRetail ?? 0) < 0 || Number(row?.planInflowCost ?? 0) < 0
      )
      if (negativeInflowMonths.length > 0) {
        messageContext.show('Cannot save: one or more months have negative inflow values. Please correct before saving.')
        return
      }
      setSavingPlanner(true)
      const startedAt = performance.now()

      const monthlyPayload = buildMonthlySavePayload()
      const weeklyPayload = buildWeeklySavePayload()

      const saveStrategy = resolvePlannerSaveStrategy({
        hasWeeklyPlanning: hasWeeklyPlanningForSelectedClass,
        monthlyPayload,
        weeklyPayload,
        isFuturePlan: false,
      })

      if (saveStrategy === 'none') {
        messageContext.show('No changes to save')
        return
      }

      if (saveStrategy === 'missing-required') {
        messageContext.show(hasWeeklyPlanningForSelectedClass
          ? 'Unable to save: missing monthly or weekly payload'
          : 'Unable to save: missing monthly payload')
        return
      }

      let newPlanVersion = null
      let saveApiMs
      if (saveStrategy === 'monthly-only') {
        const saveStartedAt = performance.now()
        const resp = await PlanService.saveMonthlyPlansWindow(clientId, monthlyPayload)
        saveApiMs = Math.round(performance.now() - saveStartedAt)
        const saved = resp?.data ?? resp
        newPlanVersion = saved?.newPlanVersion ?? predictNextPlanVersion(expectedMaxVersion)
      } else {
        // Optimistic concurrency: block if a newer version exists (normal stream < 1000).
        if (expectedMaxVersion !== null && typeof expectedMaxVersion !== 'undefined') {
          monthlyPayload.expectedMaxVersion = expectedMaxVersion
        }
        // Explicitly mark as non-future so the backend never creates a 1000-level version.
        monthlyPayload.isFuturePlan = false

        const saveStartedAt = performance.now()
        const resp = await PlanService.savePlannerWindows(clientId, { monthlyPayload, weeklyPayload })
        saveApiMs = Math.round(performance.now() - saveStartedAt)
        const saved = resp?.data ?? resp
        newPlanVersion = saved?.newPlanVersion
      }

      // After persistence, clear the in-memory session so we *must* reload from backend.
      // This prevents stale derived tables from masking a failed reload.
      setPlannerSession((s) => ({
        ...(s || {}),
        weeklyEditsByWeek: {},
        monthlyEditsByMonth: {},
        weeklyContext: null,
        monthlyContext: null,
      }))

      messageContext.showSuccess(saveStrategy === 'monthly-only'
        ? 'Saved monthly plan as a new version'
        : 'Saved plan as a new version')

      // Optimistically inject the new version row so the plan selector updates
      // immediately — before the background BQ refresh completes. This prevents
      // the planOptions effect from falling back to the old version while the
      // latestMonthlyPlans array is still stale.
      if (newPlanVersion !== null && typeof newPlanVersion !== 'undefined') {
        // Reconstruct PSID in the same format the backend uses: {lci}-{epochMonth}-{version}
        const vStr = Number(newPlanVersion).toFixed(1)
        const optimisticRow = {
          location_class_id: selectedLocationClassId,
          monthly_plan_settings_id: `${selectedLocationClassId}-${monthlyPayload?.epochMonthStart || epochMonth}-${vStr}`,
          weekly_plan_settings_id: weeklyPayload
            ? `${selectedLocationClassId}-${weeklyPayload?.epochWeekStart || 0}-${vStr}`
            : null,
          plan_version: Number(newPlanVersion),
          this_plan_epoch_month: monthlyPayload?.epochMonthStart || epochMonth,
          plan_epoch_month: monthlyPayload?.epochMonthStart || epochMonth,
          epoch_week_start: weeklyPayload?.epochWeekStart || null,
          is_future_plan: 0,
          created_date: new Date().toISOString(),
        }
        setLatestMonthlyPlans((prev) => {
          // Remove any pre-existing row for this version (shouldn't exist yet, but be safe).
          const without = (prev || []).filter((r) =>
            !(String(r?.location_class_id) === String(selectedLocationClassId) &&
              Number(r?.plan_version) === Number(newPlanVersion))
          )
          return [...without, optimisticRow]
        })
        // Only set preferredPlanVersion — do NOT call setPlanVersion directly here.
        // The planOptions effect will resolve planVersion once it sees the new row
        // in latestMonthlyPlans via preferredPlanVersion. Calling setPlanVersion(1.5)
        // before the row exists triggers a premature effect run that resets back to 1.4.
        setPreferredPlanVersion(Number(newPlanVersion))
      }

      // Background refresh — confirms the real BQ row is in latestMonthlyPlans
      // and picks up any server-side fields the optimistic row doesn't have.
      setReloadNonce((n) => n + 1)
      console.info('[planner][perf] save', {
        mode: 'normal',
        clientId,
        locationClassId: selectedLocationClassId,
        saveStrategy,
        saveApiMs,
        totalMs: Math.round(performance.now() - startedAt),
      })
    } catch (e) {
      messageContext.show(e?.response?.data?.message || 'Failed to save plan')
    } finally {
      setSavingPlanner(false)
    }
  }

  const handleUpdatePlanAsFuture = async () => {
    try {
      if (!clientId) return
      if (!hasUnsavedPlannerChanges) {
        messageContext.show('No changes to save')
        return
      }
      const negativeInflowMonths = (plannerSession?.monthlyBaseUi || []).filter(
        (row) => Number(row?.planInflowRetail ?? 0) < 0 || Number(row?.planInflowCost ?? 0) < 0
      )
      if (negativeInflowMonths.length > 0) {
        messageContext.show('Cannot save: one or more months have negative inflow values. Please correct before saving.')
        return
      }
      setSavingPlanner(true)
      const startedAt = performance.now()

      const monthlyPayload = buildMonthlySavePayload()
      const weeklyPayload = buildWeeklySavePayload()

      const saveStrategy = resolvePlannerSaveStrategy({
        hasWeeklyPlanning: hasWeeklyPlanningForSelectedClass,
        monthlyPayload,
        weeklyPayload,
        isFuturePlan: true,
      })

      if (saveStrategy === 'none') {
        messageContext.show('No changes to save')
        return
      }

      if (saveStrategy === 'monthly-only-future-unsupported') {
        messageContext.show('Future Save is not available yet for monthly-only classes in the new planner.')
        return
      }

      if (saveStrategy === 'missing-required') {
        messageContext.show('Unable to save: missing monthly or weekly payload')
        return
      }

      monthlyPayload.isFuturePlan = true

      // Optimistic concurrency for future stream (>= 1000).
      // If no future exists yet, expectedMaxVersionFuture will be null and backend will allow initial 1000.0.
      if (expectedMaxVersionFuture !== null && typeof expectedMaxVersionFuture !== 'undefined') {
        monthlyPayload.expectedMaxVersion = expectedMaxVersionFuture
      }

      const saveStartedAt = performance.now()
      const resp = await PlanService.savePlannerWindows(clientId, { monthlyPayload, weeklyPayload })
      const saveApiMs = Math.round(performance.now() - saveStartedAt)
      const saved = resp?.data ?? resp
      const newPlanVersion = saved?.newPlanVersion

      setPlannerSession((s) => ({
        ...(s || {}),
        weeklyEditsByWeek: {},
        monthlyEditsByMonth: {},
        weeklyContext: null,
        monthlyContext: null,
      }))

      messageContext.showSuccess('Saved future plan as a new version')

      // Optimistically inject the new future version row (>= 1000) for immediate UI update.
      if (newPlanVersion !== null && typeof newPlanVersion !== 'undefined') {
        const optimisticRow = {
          location_class_id: selectedLocationClassId,
          monthly_plan_settings_id: `${selectedLocationClassId}-${monthlyPayload?.epochMonthStart || epochMonth}-${Number(newPlanVersion).toFixed(1)}`,
          weekly_plan_settings_id: `${selectedLocationClassId}-${weeklyPayload?.epochWeekStart || 0}-${Number(newPlanVersion).toFixed(1)}`,
          plan_version: Number(newPlanVersion),
          this_plan_epoch_month: monthlyPayload?.epochMonthStart || epochMonth,
          plan_epoch_month: monthlyPayload?.epochMonthStart || epochMonth,
          epoch_week_start: weeklyPayload?.epochWeekStart || null,
          is_future_plan: 1,
          created_date: new Date().toISOString(),
        }
        setLatestMonthlyPlans((prev) => {
          const without = (prev || []).filter((r) =>
            !(String(r?.location_class_id) === String(selectedLocationClassId) &&
              Number(r?.plan_version) === Number(newPlanVersion))
          )
          return [...without, optimisticRow]
        })
        setPreferredPlanVersion(Number(newPlanVersion))
      }

      setReloadNonce((n) => n + 1)
      console.info('[planner][perf] save', {
        mode: 'future',
        clientId,
        locationClassId: selectedLocationClassId,
        saveStrategy,
        saveApiMs,
        totalMs: Math.round(performance.now() - startedAt),
      })
    } catch (e) {
      messageContext.show(e?.response?.data?.message || 'Failed to save future plan')
    } finally {
      setSavingPlanner(false)
    }
  }


  const moveLocation = ({ forward = true } = {}) => {
    if (!locationOptions?.length) {
      messageContext.show('No locations available')
      return
    }

    const idx = locationOptions.findIndex((l) => String(l.location_id) === String(selectedLocationId))
    const nextIdx = idx === -1
      ? 0
      : (forward ? (idx + 1) % locationOptions.length : (idx - 1 + locationOptions.length) % locationOptions.length)

    const nextLocationId = locationOptions[nextIdx].location_id
    setSelectedLocationId(nextLocationId)
    // Clear downstream selections; effects will re-populate.
    setSelectedLocationClassId(0)
    resetPlanSelection()
    setForceReload(false)
  }

  const moveClass = ({ forward = true } = {}) => {
    if (!classOptions?.length) {
      messageContext.show('No classes available for this location')
      return
    }

    const idx = classOptions.findIndex((c) => String(c.class_id) === String(selectedLocationClassId))
    const nextIdx = idx === -1
      ? 0
      : (forward ? (idx + 1) % classOptions.length : (idx - 1 + classOptions.length) % classOptions.length)

    const nextClassId = classOptions[nextIdx].class_id
    setSelectedLocationClassId(nextClassId)
    // Clear plan selection and allow planOptions effect to select latest.
    resetPlanSelection()
    setForceReload(false)
  }

  useEffect(() => {
    safeLsSet(STORAGE_KEYS.locationId, selectedLocationId)
  }, [selectedLocationId])

  useEffect(() => {
    safeLsSet(STORAGE_KEYS.locationClassId, selectedLocationClassId)
  }, [selectedLocationClassId])

  useEffect(() => {
    safeLsSet(STORAGE_KEYS.planId, selectedPlanOptionValue)
  }, [selectedPlanOptionValue])

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const resp = await ClientService.listClientsSimple()
        setClientList(Array.isArray(resp) ? resp : [])
      } catch (e) {
        // Non-blocking for planning
        setClientList([])
      }
    }
    fetchClients()
  }, [])

  const panelCanLoad = useMemo(() => {
    return !!(currentClientContext?.client && epochMonth && selectedPlanId && selectedLocationClassId)
  }, [currentClientContext?.client, epochMonth, selectedPlanId, selectedLocationClassId])

  const handlePanelOverridePlanData = () => {
    // The panel's copy-from uses legacy PlanEditTable override logic.
    // The new weekly/monthly shells don't yet expose a unified override contract.
    messageContext.show('Loaded copy data into the legacy panel. Apply changes in the grid views as needed.')
  }

  // panelChartData is populated by the hidden legacy embed and can be used later for targets/copy features.
  // Keep it to satisfy LivePlanEditEmbed callbacks.
  void panelChartData

  useEffect(() => {
    // intentionally empty — keeping effect for potential future plannerSession watchers
  }, [plannerSession])
  const weeklyShellRevision = useMemo(() => {
    const monthSig = Object.entries(plannerSession?.monthlyEditsByMonth || {})
      .map(([k, v]) => `${k}:${v?.ts || 0}`)
      .sort()
      .join('|')
    const weekSig = Object.entries(plannerSession?.weeklyEditsByWeek || {})
      .map(([k, v]) => `${k}:${v?.ts || 0}`)
      .sort()
      .join('|')
    const dailySig = `${plannerSession?.dailyByDate === plannerSession?.dailyIndex?.byDate ? 'base' : 'draft'}:${plannerSession?.dailyByDate?.size || 0}`
    return [monthSig, weekSig, dailySig].join('::')
  }, [plannerSession])
  void weeklyShellRevision

  if (!featureEnabled) {
    // The useEffect above will navigate to live-plan-edit once flags are hydrated.
    // Show a brief loading state rather than a dead-end error while the redirect fires.
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">
          {plannerFlagsHydrated ? 'Redirecting to legacy planner…' : 'Loading planner…'}
        </Typography>
      </Box>
    )
  }


  return (
    <Box sx={ModifyStyle.mainContainer} data-testid="planner-page">
      <Box sx={{ ...NewPlanStyle.filterHeader, mb: 0 }}>
        <Box style={{ ...ModifyStyle.alignCenter, ...NewPlanStyle.textFields }}>
          <Box sx={ModifyStyle.textStyle}>
            <Box
              component="select"
              data-testid="planner-filter-epoch-month"
              sx={{
                mx: 0.5,
                height: '30px',
                backgroundColor: 'transparent',
                color: 'white',
                fontWeight: 'bold',
                borderColor: 'white',
                option: { backgroundColor: '#5c4174!important' },
              }}
              onChange={(e) => setEpochMonth(e.target.value)}
              value={epochMonth}
            >
              {epochMonthOptions.map((item) => (
                <option key={`epoch_month_item_${item}`} value={item}>
                  {PlanService.getEpochMonthAsMMMYY(item)}
                </option>
              ))}
            </Box>
          </Box>

          <Box sx={ModifyStyle.textStyle}>
            <Box
              component="select"
              data-testid="planner-filter-location"
              sx={{
                mx: 0.5,
                height: '30px',
                backgroundColor: 'transparent',
                color: 'white',
                fontWeight: 'bold',
                borderColor: 'rgba(255,255,255,0.6)',
                option: { backgroundColor: '#5c4174!important' },
              }}
              onChange={(e) => {
                setSelectedLocationId(Number(e.target.value) || 0)
                setSelectedLocationClassId(0)
                resetPlanSelection()
              }}
              value={selectedLocationId}
            >
              <option value={0}>Select Location</option>
              {locationOptions.map((item) => (
                <option key={`location_item_${item.location_id}`} value={item.location_id}>
                  {item.name} - {item.code}
                </option>
              ))}
            </Box>
          </Box>

          {selectedLocationId ? (
            <Box sx={ModifyStyle.textStyle}>
              <Box
                component="select"
                data-testid="planner-filter-class"
                sx={{
                  mx: 0.5,
                  height: '30px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  fontWeight: 'bold',
                  borderColor: 'rgba(255,255,255,0.6)',
                  option: { backgroundColor: '#5c4174!important' },
                }}
                onChange={(e) => {
                  setSelectedLocationClassId(Number(e.target.value) || 0)
                  resetPlanSelection()
                }}
                value={selectedLocationClassId}
              >
                <option value={0}>Select Class</option>
                {classOptions.map((item) => (
                  <option key={`class_item_${item.class_id}`} value={item.class_id}>
                    {item.name} - {item.code}
                  </option>
                ))}
              </Box>
            </Box>
          ) : null}

          {planOptions.length ? (
            <Box sx={ModifyStyle.textStyle}>
              <Box
                component="select"
                data-testid="planner-filter-version"
                sx={{
                  mx: 0.5,
                  height: '30px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  fontWeight: 'bold',
                  borderColor: 'rgba(255,255,255,0.6)',
                  option: { backgroundColor: '#5c4174!important' },
                }}
                onChange={(e) => {
                  const nextValue = e.target.value
                  setSelectedPlanOptionValue(nextValue)
                  const p = planOptions.find((x) => String(x.optionValue) === String(nextValue))
                  setSelectedPlanId(p?.plan_id || 0)
                  setPlanVersion(p?.plan_version || null)
                  setMonthlyPlanSettingsId(p?.monthly_plan_settings_id || p?.plan_settings_id || null)
                  setWeeklyPlanSettingsId(p?.weekly_plan_settings_id || null)
                  lastPlanSelectionClientIdRef.current = Number(clientId || 0)
                  lastPlanSelectionLocationClassIdRef.current = Number(selectedLocationClassId || 0)
                }}
                value={selectedPlanOptionValue}
              >
                <option value={0}>Select Version</option>
                {planOptions.map((item) => (
                  <option key={`plan_item_${item.optionValue}`} value={item.optionValue}>
                    {item.plan_version}
                  </option>
                ))}
              </Box>
            </Box>
          ) : null}

          <Box sx={ModifyStyle.textStyle}>
            <Button
              variant="outlined"
              sx={{ color: 'white' }}
              data-testid="planner-filter-go"
              onClick={() => {
                if (selectedLocationId && selectedLocationClassId) {
                  // Reset version selection so the effect auto-picks the highest version < 1000
                  resetPlanSelection()
                  // Increment nonce: forces latestMonthlyPlans refetch AND editor remount
                  setReloadNonce((n) => n + 1)
                } else {
                  messageContext.show('Please select location and class')
                }
              }}
            >
              Go
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2, pb: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="h5">{title} — Plan Edit (Weekly UI)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View and editing mode are not persisted to the database until Save.
            </Typography>
            {planVersion !== null && typeof planVersion !== 'undefined' ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
                data-testid="planner-selected-version-meta"
              >
                Version {planVersion}{selectedPlanCreatedLabel ? ` • Created ${selectedPlanCreatedLabel}` : ''}
              </Typography>
            ) : null}
          </Box>

          <PlanViewModeToggle
            viewMode={resolvedViewMode}
            onViewModeChange={setViewMode}
            calcMode={calcMode}
            onCalcModeChange={setCalcMode}
          />
        </Box>

        {/* Keep both editors mounted to prevent refetch and preserve draft edits when switching tabs */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', position: 'relative' }}>
          <Tooltip title={leftPanelCollapsed ? 'Show context panel' : 'Hide context panel'}>
            <IconButton
              data-testid="planner-left-panel-toggle"
              size="small"
              onClick={() => setLeftPanelCollapsed((prev) => !prev)}
              sx={{
                position: 'absolute',
                top: 8,
                left: leftPanelCollapsed ? 8 : 318,
                zIndex: 4,
                backgroundColor: '#fff',
                border: '1px solid #d1d5db',
                boxShadow: '0 1px 3px rgba(15,23,42,0.15)',
                transition: 'left 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              {leftPanelCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              flex: leftPanelCollapsed ? '0 0 0px' : '0 0 350px',
              width: leftPanelCollapsed ? 0 : 350,
              minWidth: leftPanelCollapsed ? 0 : 320,
              overflow: 'hidden',
              transition: 'all 0.2s ease',
            }}
          >
            {!leftPanelCollapsed ? (
              <PlanLeftPanel>
                <PlanLeftPanelControls
                  epochMonth={epochMonth}
                  clientList={clientList}
                  tabs={panelTabs}
                  chartData={resolvedViewMode === 'weekly' ? weeklyChartData : panelChartData}
                  periodData={resolvedViewMode === 'weekly' ? weeklyPeriodData : null}
                  chartLabels={resolvedViewMode === 'weekly' ? weeklyChartLabels : monthLabels}
                  chartXKeyLabel={resolvedViewMode === 'weekly' ? 'week' : 'month'}
                  enableChart={true}
                  onOverridePlanData={handlePanelOverridePlanData}
                />
              </PlanLeftPanel>
            ) : null}
          </Box>

          {/* Hidden legacy engine — kept only for its copy-from and save pipeline.
              Chart data is driven from handleMonthlyBaseUi; tabs are also derived
              there so we intentionally do NOT pass onTabs here — the legacy embed
              reads from the reporting pipeline (stale) and would overwrite the
              live plan values we already populated. */}
          {panelCanLoad ? (
            <Box sx={{ display: 'none' }}>
              <LivePlanEditEmbed
                client={currentClientContext.client}
                epochMonth={epochMonth}
                locationClassId={selectedLocationClassId}
                planId={selectedPlanId}
                planVersion={planVersion}
              />
            </Box>
          ) : null}

          <Box sx={{ display: resolvedViewMode === 'weekly' ? 'block' : 'none', flex: 1, minWidth: 0 }}>
            <PlanEditWeeklyTableShell
              key={`weekly.${clientId}.${selectedLocationId}.${selectedLocationClassId}.${monthlyPlanSettingsId || 'na'}.${weeklyPlanSettingsId || 'na'}.v${planVersion ?? '0'}.r${reloadNonce}`}
              calcMode={calcMode}
              annualHeaderCollapsed={annualHeaderCollapsed}
              onAnnualHeaderCollapsedChange={setAnnualHeaderCollapsed}
              storageKeySeed={`${clientId}.${selectedLocationId}.${selectedLocationClassId}.${monthlyPlanSettingsId || 'n/a'}.${weeklyPlanSettingsId || 'n/a'}`}
              clientId={clientId}
              locationClassId={selectedLocationClassId}
              epochWeek={epochWeek}
              monthlyPlanSettingsId={monthlyPlanSettingsId}
              weeklyPlanSettingsId={weeklyPlanSettingsId}
              onChartData={setWeeklyChartData}
              onChartLabels={setWeeklyChartLabels}
              onPeriodData={setWeeklyPeriodData}
              plannerSession={plannerSession}
              onWeeklyEdit={updateWeeklyEdit}
              onWeeklyContextLoaded={handleWeeklyContextLoaded}
              onDailyByDateChange={handleDailyByDateChange}
            />
          </Box>

          <Box sx={{ display: resolvedViewMode === 'monthly' ? 'block' : 'none', flex: 1, minWidth: 0 }}>
            <PlanEditMonthlyTableShell
              key={`monthly.${clientId}.${selectedLocationId}.${selectedLocationClassId}.${monthlyPlanSettingsId || 'na'}.v${planVersion ?? '0'}.r${reloadNonce}`}
              client={currentClientContext.client}
              epochMonth={epochMonth}
              planId={selectedPlanId}
              annualHeaderCollapsed={annualHeaderCollapsed}
              onAnnualHeaderCollapsedChange={setAnnualHeaderCollapsed}
              monthlyPlanSettingsId={monthlyPlanSettingsId}
              planVersion={planVersion}
              locationClassId={selectedLocationClassId}
              plannerSession={plannerSession}
              weeklyPeriodData={weeklyPeriodData}
              onMonthlyEdit={updateMonthlyEdit}
              onMonthlyContextLoaded={handleMonthlyContextLoaded}
              onMonthlyBaseUi={handleMonthlyBaseUi}
            />
          </Box>
        </Box>

        {/* Legacy monthly embed for non-weekly view is intentionally not used here; legacy planning stays unchanged elsewhere. */}
      </Box>

      <PlanEditActionBar
        savingPlan={savingPlanner}
        saveDisabled={savingPlanner}
        onUpdate={handleUpdatePlan}
        onUpdateAsFuture={handleUpdatePlanAsFuture}
        nextLocation={() => moveLocation({ forward: true })}
        prevLocation={() => moveLocation({ forward: false })}
        nextClass={() => moveClass({ forward: true })}
        prevClass={() => moveClass({ forward: false })}
        leftPanelCollapsed={leftPanelCollapsed}
      />
    </Box>
  )
}

export default LivePlanModifyWeekly
